import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Reveal } from "../components/Reveal";
import {
  Camera, MonitorCheck, Lock, ShieldAlert, FileCheck2, Fingerprint,
  ArrowRight, CheckCircle2, BarChart3, ClipboardCheck, Timer, Quote
} from "lucide-react";

const Eyebrow = ({ children }) => (
  <span className="inline-flex items-center gap-2 text-xs font-bold tracking-widest text-[#15348C] bg-[#15348C]/[0.06] border border-[#15348C]/10 px-4 py-1.5 rounded-full">
    <span className="w-1.5 h-1.5 rounded-full bg-[#F57C20]"/>
    {children}
  </span>
);

const SECURITY_FEATURES = [
  { icon: Camera, title: "Live Webcam Monitoring", desc: "AI-assisted video monitoring flags suspicious movement, multiple faces or absence from frame in real time." },
  { icon: MonitorCheck, title: "Browser Lockdown", desc: "Secure browser blocks tab-switching, copy-paste, screen-sharing and unauthorised applications during the test." },
  { icon: Fingerprint, title: "Identity Verification", desc: "Government-ID and face-match verification confirms the right candidate is taking the assessment." },
  { icon: ShieldAlert, title: "AI Anomaly Flagging", desc: "Behavioural and audio analytics surface risk scores for human reviewers to act on post-exam." },
  { icon: FileCheck2, title: "Question Randomisation", desc: "Dynamic question banks and shuffled options minimise collusion and content leakage." },
  { icon: Lock, title: "Encrypted Data Handling", desc: "End-to-end encrypted recordings and results, with configurable retention and access controls." }
];

const HOW_IT_WORKS = [
  { stage: "Before", title: "Setup & Verification", desc: "Candidates complete a system check, ID verification and environment scan before the timer starts." },
  { stage: "During", title: "Live Monitoring", desc: "AI and human proctors watch for integrity signals while the candidate completes the assessment." },
  { stage: "After", title: "Review & Reporting", desc: "Flagged incidents, session recordings and a integrity score are delivered with the results dashboard." }
];

const USE_CASES = [
  "Campus & Lateral Hiring Assessments",
  "Vendor Certification Exams",
  "Internal Skill Audits",
  "Regulatory & Compliance Testing"
];

const STATS = [
  { icon: BarChart3, value: "99.6%", label: "Exam Integrity Score" },
  { icon: ClipboardCheck, value: "2M+", label: "Proctored Sessions Delivered" },
  { icon: Timer, value: "<30s", label: "Avg. Candidate Verification Time" }
];

const ProctoredAssessmentsPage = () => {
  return (
    <div className="bg-white min-h-screen">
      <Header/>

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-[#0A1F5C] via-[#15348C] to-[#1E47A8] text-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "34px 34px" }}
        />
        <div className="absolute -top-24 -right-16 w-80 h-80 bg-[#6BBE32]/20 rounded-full blur-[100px]"/>
        <div className="absolute -bottom-24 -left-16 w-80 h-80 bg-[#F57C20]/20 rounded-full blur-[100px]"/>
        <div className="relative max-w-5xl mx-auto px-4 py-24 lg:py-28 text-center">
          <Reveal>
            <span className="inline-flex items-center gap-2 text-xs font-bold tracking-widest text-[#8BD835] bg-white/5 border border-white/10 px-4 py-2 rounded-full mb-7">
              <Lock size={14}/> SECURE PROCTORED ASSESSMENTS
            </span>
            <h1 className="text-4xl lg:text-6xl font-bold leading-[1.1]">Exam integrity you and your candidates can trust.</h1>
            <p className="mt-5 text-gray-300 text-base lg:text-lg max-w-2xl mx-auto leading-relaxed">
              AI and human-reviewed proctoring for hiring, certification and compliance assessments &mdash; secure by design, fair by default.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/contact" className="sg-btn-glow inline-flex items-center gap-2 bg-[#F57C20] hover:bg-[#D86810] text-white font-semibold px-6 py-3.5 rounded-lg transition-colors">
                Request a Proctoring Demo <ArrowRight size={16}/>
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Security feature grid */}
      <section className="bg-white py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-14">
              <Eyebrow>BUILT-IN SECURITY</Eyebrow>
              <h2 className="mt-4 text-3xl lg:text-4xl font-bold text-[#15348C]">Every layer designed to protect exam integrity.</h2>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SECURITY_FEATURES.map((f, i) => (
              <Reveal key={f.title} variant="up" delay={(i % 3) * 100}>
                <div className="sg-card group bg-white border border-gray-200/80 rounded-2xl p-7 h-full shadow-sm">
                  <div className="w-12 h-12 rounded-xl bg-[#15348C] flex items-center justify-center mb-5 group-hover:bg-[#6BBE32] transition-colors">
                    <f.icon size={22} className="text-white"/>
                  </div>
                  <h4 className="font-bold text-[#15348C] text-lg">{f.title}</h4>
                  <p className="text-sm text-gray-500 mt-2.5 leading-relaxed">{f.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-[#F8F9FA] py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-14">
              <Eyebrow>HOW PROCTORING WORKS</Eyebrow>
              <h2 className="mt-4 text-3xl lg:text-4xl font-bold text-[#15348C]">Three stages, one continuous chain of custody.</h2>
            </div>
          </Reveal>
          <div className="grid lg:grid-cols-3 gap-6">
            {HOW_IT_WORKS.map((s, i) => (
              <Reveal key={s.stage} variant="up" delay={i * 100}>
                <div className="bg-white border border-gray-200/80 rounded-2xl p-7 h-full shadow-sm relative">
                  <div className="absolute top-6 right-6 text-4xl font-bold text-[#15348C]/[0.06]">{String(i + 1).padStart(2, "0")}</div>
                  <div className="text-xs font-bold tracking-widest text-[#F57C20]">{s.stage.toUpperCase()}</div>
                  <h3 className="text-lg font-bold text-[#15348C] mt-1">{s.title}</h3>
                  <p className="mt-2.5 text-sm text-gray-500 leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Use cases + stats */}
      <section className="bg-white py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-10">
          <Reveal variant="left">
            <h2 className="text-3xl lg:text-4xl font-bold text-[#15348C]">Wherever exam integrity is non-negotiable.</h2>
            <div className="mt-6 space-y-3">
              {USE_CASES.map(u => (
                <div key={u} className="flex items-center gap-3 bg-[#F8F9FA] border border-gray-200/80 rounded-xl px-5 py-4">
                  <CheckCircle2 size={18} className="text-[#6BBE32] shrink-0"/>
                  <span className="font-semibold text-[#15348C] text-sm">{u}</span>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal variant="right" delay={100}>
            <div className="bg-gradient-to-br from-[#0A1F5C] to-[#1E47A8] text-white rounded-2xl p-8 h-full relative overflow-hidden">
              <div className="absolute -bottom-16 -left-16 w-48 h-48 bg-[#F57C20]/20 rounded-full blur-3xl"/>
              <div className="relative">
                <div className="text-xs font-bold tracking-widest text-[#8BD835] mb-6">TRUSTED AT SCALE</div>
                <div className="space-y-6">
                  {STATS.map(s => (
                    <div key={s.label} className="flex items-center gap-4">
                      <div className="w-11 h-11 shrink-0 rounded-lg bg-white/10 flex items-center justify-center">
                        <s.icon size={20} className="text-[#8BD835]"/>
                      </div>
                      <div>
                        <div className="text-2xl font-bold">{s.value}</div>
                        <div className="text-xs text-gray-300">{s.label}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Testimonial */}
      <section className="bg-[#F8F9FA] py-16">
        <div className="max-w-4xl mx-auto px-4">
          <Reveal variant="scale">
            <div className="bg-white border border-gray-200/80 rounded-2xl p-8 lg:p-10 text-center shadow-sm">
              <Quote size={30} className="text-[#F57C20] mx-auto mb-4"/>
              <p className="text-lg lg:text-xl text-[#15348C] leading-relaxed">
                Moving our certification exams to SkillsGain&rsquo;s proctored platform eliminated the integrity disputes we used to see every cycle &mdash; and candidates trust the process more.
              </p>
              <div className="mt-6">
                <div className="font-bold text-[#15348C]">Director of Assessments</div>
                <div className="text-sm text-gray-500">National Certification Board</div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-white py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal variant="scale">
            <div className="bg-gradient-to-br from-[#15348C] to-[#0A1F5C] rounded-3xl p-10 lg:p-16 relative overflow-hidden">
              <div className="absolute -top-20 -right-20 w-64 h-64 bg-[#6BBE32]/20 rounded-full blur-3xl"/>
              <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-[#F57C20]/20 rounded-full blur-3xl"/>
              <div className="relative grid lg:grid-cols-2 gap-10 items-center">
                <div>
                  <div className="text-xs font-bold tracking-widest text-[#8BD835] mb-3">FOR HR, TALENT &amp; CERTIFICATION TEAMS</div>
                  <h2 className="text-3xl lg:text-4xl font-bold text-white leading-tight">Secure your next assessment cycle.</h2>
                  <p className="mt-4 text-gray-300 leading-relaxed">See the proctoring dashboard, integrity scoring and candidate experience in a live walkthrough.</p>
                  <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2.5 text-xs text-gray-300">
                    {["AI + human review", "Configurable integrity rules", "Detailed audit trail"].map(t => (
                      <span key={t} className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-[#8BD835]"/>{t}</span>
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap gap-3 lg:justify-end">
                  <Link to="/contact" className="sg-btn-glow inline-flex items-center gap-2 bg-[#F57C20] hover:bg-[#D86810] text-white font-semibold px-6 py-3.5 rounded-lg transition-colors">
                    Talk to Our Advisor <ArrowRight size={16}/>
                  </Link>
                  <Link to="/contact" className="border-2 border-white/30 text-white font-semibold px-6 py-3.5 rounded-lg hover:bg-white/10 hover:border-[#8BD835] transition-colors">Request a Proposal</Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer/>
    </div>
  );
};

export default ProctoredAssessmentsPage;
